<template>
	<v-container fluid class="pa-4">
		<v-row>
			<!-- Benvenuto -->
			<v-col cols="12">
				<v-card flat class="mb-2">
					<v-card-text>
						<div class="text-h5 font-weight-bold mb-1">
							Benvenuto, {{ userName }}
						</div>
						<div class="text-body-1 grey--text">
							{{ companyName }}
						</div>
					</v-card-text>
				</v-card>
			</v-col>

			<!-- Card statistiche -->
			<v-col cols="12" sm="6" md="3">
				<v-card outlined class="text-center pa-4" @click="$router.push('/portal/documents')">
					<v-icon size="40" color="primary" class="mb-2">mdi-file-document-multiple</v-icon>
					<div class="text-h4 font-weight-bold">{{ stats.documents }}</div>
					<div class="text-body-2 grey--text">Documenti</div>
				</v-card>
			</v-col>
			<v-col cols="12" sm="6" md="3">
				<v-card outlined class="text-center pa-4 cursor-pointer" @click="showExpiringDialog = true">
					<v-icon size="40" color="orange" class="mb-2">mdi-calendar-alert</v-icon>
					<div class="text-h4 font-weight-bold">{{ expiringDocs.length }}</div>
					<div class="text-body-2 grey--text">In scadenza (7gg)</div>
				</v-card>
			</v-col>
			<v-col cols="12" sm="6" md="3">
				<v-card outlined class="text-center pa-4" @click="$router.push('/portal/notifications')">
					<v-icon size="40" color="red" class="mb-2">mdi-bell</v-icon>
					<div class="text-h4 font-weight-bold">{{ stats.unread }}</div>
					<div class="text-body-2 grey--text">Notifiche non lette</div>
				</v-card>
			</v-col>
			<v-col cols="12" sm="6" md="3">
				<v-card outlined class="text-center pa-4">
					<v-icon size="40" color="green" class="mb-2">mdi-cloud-upload</v-icon>
					<div class="text-h4 font-weight-bold">{{ stats.myUploads }}</div>
					<div class="text-body-2 grey--text">File caricati da te</div>
				</v-card>
			</v-col>

			<!-- Spazio utilizzato -->
			<v-col cols="12" v-if="storage.quota">
				<v-card outlined class="pa-4">
					<div class="d-flex align-center mb-2">
						<v-icon color="primary" class="mr-2">mdi-harddisk</v-icon>
						<span class="text-subtitle-1 font-weight-bold">Spazio di archiviazione</span>
						<v-spacer></v-spacer>
						<span class="text-body-2">
							{{ formatSize(storage.total) }} / {{ storage.quota }} MB
						</span>
					</div>
					<v-progress-linear
						:value="storagePercent"
						:color="storagePercent > 90 ? 'red' : storagePercent > 70 ? 'orange' : 'primary'"
						height="20"
						rounded
					>
						<template v-slot:default>
							<span class="white--text text-caption font-weight-bold">{{ storagePercent }}%</span>
						</template>
					</v-progress-linear>
				</v-card>
			</v-col>

			<!-- Documenti recenti -->
			<v-col cols="12" md="6">
				<v-card outlined>
					<v-card-title class="text-subtitle-1 font-weight-bold">
						<v-icon left small>mdi-clock-outline</v-icon>
						Documenti recenti
					</v-card-title>
					<v-divider></v-divider>
					<v-list dense v-if="recentDocs.length">
						<v-list-item
							v-for="doc in recentDocs"
							:key="doc.id"
							@click="downloadDoc(doc)"
						>
							<v-list-item-avatar>
								<v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title>{{ doc.name | fileName(doc.ext) }}</v-list-item-title>
								<v-list-item-subtitle>{{ formatDate(doc.created_at) }}</v-list-item-subtitle>
							</v-list-item-content>
							<v-list-item-action>
								<v-icon small>mdi-download</v-icon>
							</v-list-item-action>
						</v-list-item>
					</v-list>
					<v-card-text v-else class="text-center grey--text">
						Nessun documento recente
					</v-card-text>
				</v-card>
			</v-col>

			<!-- Attività recenti -->
			<v-col cols="12" md="6">
				<v-card outlined>
					<v-card-title class="text-subtitle-1 font-weight-bold">
						<v-icon left small>mdi-history</v-icon>
						Attività recenti
					</v-card-title>
					<v-divider></v-divider>
					<v-list dense v-if="recentActivity.length">
						<v-list-item
							v-for="act in recentActivity"
							:key="act.id"
						>
							<v-list-item-avatar>
								<v-icon small :color="getActionColor(act.action)">{{ getActionIcon(act.action) }}</v-icon>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title>{{ getActionLabel(act.action) }}</v-list-item-title>
								<v-list-item-subtitle>
									{{ act.user ? act.user.name : '' }} - {{ formatDate(act.created_at) }}
								</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
					</v-list>
					<v-card-text v-else class="text-center grey--text">
						Nessuna attività recente
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<!-- Dialog documenti in scadenza -->
		<v-dialog v-model="showExpiringDialog" max-width="600">
			<v-card>
				<v-card-title class="d-flex align-center">
					<v-icon left color="orange">mdi-calendar-alert</v-icon>
					Documenti in scadenza
				</v-card-title>
				<v-divider></v-divider>
				<v-list dense v-if="expiringDocs.length">
					<v-list-item
						v-for="doc in expiringDocs"
						:key="doc.id"
						@click="downloadDoc(doc)"
					>
						<v-list-item-avatar>
							<v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
						</v-list-item-avatar>
						<v-list-item-content>
							<v-list-item-title>{{ doc.name | fileName(doc.ext) }}</v-list-item-title>
							<v-list-item-subtitle>
								Scadenza: <strong>{{ formatDateShort(doc.expires_at) }}</strong>
							</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<v-icon small>mdi-download</v-icon>
						</v-list-item-action>
					</v-list-item>
				</v-list>
				<v-card-text v-else class="text-center grey--text pa-8">
					Nessun documento in scadenza nei prossimi 7 giorni
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="showExpiringDialog = false">Chiudi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
export default {
	name: 'PortalDashboard',
	data() {
		return {
			stats: {
				documents: 0,
				folders: 0,
				unread: 0,
				myUploads: 0
			},
			recentDocs: [],
			recentActivity: [],
			expiringDocs: [],
			showExpiringDialog: false,
			storage: { total: 0, quota: null }
		}
	},
	computed: {
		userName() {
			const user = this.$store.getters.portalUser
			return user ? user.name : ''
		},
		companyName() {
			const company = this.$store.getters.portalCurrentCompany
			if (company) return company.name
			const user = this.$store.getters.portalUser
			return user && user.company ? user.company.business_name || user.company.name : ''
		},
		storagePercent() {
			if (!this.storage.quota) return 0
			const quotaBytes = this.storage.quota * 1024 * 1024
			return Math.min(100, Math.round((this.storage.total / quotaBytes) * 100))
		}
	},
	created() {
		this.loadDashboard()
	},
	methods: {
		async loadDashboard() {
			try {
				const [docsResp, actResp, folderResp, expiringResp] = await Promise.all([
					this.axios.get('portal/documents', { params: { rowsPerPage: 5 } }),
					this.axios.get('portal/activity', { params: { rowsPerPage: 10 } }),
					this.axios.get('portal/folders'),
					this.axios.get('portal/documents/expiring')
				])

				if (docsResp.data.success) {
					this.recentDocs = docsResp.data.data || []
					this.stats.documents = docsResp.data.total || 0
				}
				if (actResp.data.success) {
					this.recentActivity = actResp.data.data || []
				}
				if (folderResp.data.success) {
					this.stats.folders = (folderResp.data.data.folders || []).length
					if (folderResp.data.data.storage) {
						this.storage = folderResp.data.data.storage
					}
				}
				if (expiringResp.data.success) {
					this.expiringDocs = expiringResp.data.data || []
				}

				this.stats.unread = this.$store.getters.portalUnreadNotifications

				// Conta file caricati dall'utente
				const user = this.$store.getters.portalUser
				if (user) {
					this.stats.myUploads = this.recentDocs.filter(d => d.uploaded_by === user.id).length
				}
			} catch (e) {
				// silenzioso
			}
		},
		async downloadDoc(doc) {
			try {
				const response = await this.axios.get('portal/documents/' + doc.id + '/download', {
					responseType: 'blob',
					headers: { Accept: '*/*' }
				})
				const blob = new Blob([response.data], { type: response.headers['content-type'] })
				const url = window.URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = doc.name.endsWith('.' + doc.ext) ? doc.name : doc.name + '.' + doc.ext
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (e) {
				this.$store.commit('setSnackbarError')
			}
		},
		formatSize(bytes) {
			if (!bytes) return '0 MB'
			const mb = bytes / (1024 * 1024)
			if (mb < 1) return mb.toFixed(2) + ' MB'
			if (mb < 1024) return mb.toFixed(1) + ' MB'
			return (mb / 1024).toFixed(2) + ' GB'
		},
		formatDate(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('it-IT', {
				day: '2-digit', month: '2-digit', year: 'numeric',
				hour: '2-digit', minute: '2-digit'
			})
		},
		formatDateShort(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('it-IT', {
				day: '2-digit', month: 'short', year: 'numeric'
			})
		},
		getFileIcon(ext) {
			const map = {
				pdf: 'mdi-file-pdf-box',
				doc: 'mdi-file-word-box', docx: 'mdi-file-word-box',
				xls: 'mdi-file-excel-box', xlsx: 'mdi-file-excel-box',
				ppt: 'mdi-file-powerpoint-box', pptx: 'mdi-file-powerpoint-box',
				jpg: 'mdi-file-image', jpeg: 'mdi-file-image', png: 'mdi-file-image', gif: 'mdi-file-image',
				zip: 'mdi-folder-zip', rar: 'mdi-folder-zip', '7z': 'mdi-folder-zip',
				txt: 'mdi-file-document', csv: 'mdi-file-delimited',
				xml: 'mdi-file-code', json: 'mdi-file-code',
				p7m: 'mdi-file-certificate'
			}
			return map[ext] || 'mdi-file'
		},
		getFileColor(ext) {
			const map = {
				pdf: 'red', doc: 'blue', docx: 'blue',
				xls: 'green', xlsx: 'green',
				jpg: 'purple', jpeg: 'purple', png: 'purple',
				zip: 'amber', p7m: 'teal'
			}
			return map[ext] || 'grey'
		},
		getActionIcon(action) {
			const map = {
				viewed: 'mdi-eye', downloaded: 'mdi-download', uploaded: 'mdi-upload',
				renamed: 'mdi-pencil', moved: 'mdi-folder-move', deleted: 'mdi-delete',
				tagged: 'mdi-tag', version_uploaded: 'mdi-history', logged_in: 'mdi-login'
			}
			return map[action] || 'mdi-information'
		},
		getActionColor(action) {
			const map = {
				uploaded: 'green', downloaded: 'blue', deleted: 'red',
				viewed: 'grey', logged_in: 'primary'
			}
			return map[action] || 'grey'
		},
		getActionLabel(action) {
			const map = {
				viewed: 'Visualizzazione', downloaded: 'Download', uploaded: 'Caricamento',
				renamed: 'Rinominato', moved: 'Spostato', deleted: 'Eliminato',
				tagged: 'Tag modificato', version_uploaded: 'Nuova versione', logged_in: 'Accesso'
			}
			return map[action] || action
		}
	}
}
</script>
